import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from '@/shared/api'
import { type CursorPage, nextCursor } from '@/shared/lib'
import {
  type DocumentOwnerKind,
  DocumentReadSchema,
  DocumentSummarySchema,
  DocumentVersionSchema,
  DocumentViewEntrySchema,
} from '../model/types'

export const documentKeys = {
  byEntity: (kind: DocumentOwnerKind, id: string, scope?: 'with-projects') =>
    ['document', 'entity', kind, id, ...(scope ? [scope] : [])] as const,
  detail: (id: string) => ['document', 'detail', id] as const,
  versions: (id: string) => ['document', 'versions', id] as const,
  views: (id: string) => ['document', 'views', id] as const,
}

// Плоский листинг документов сущности (со срезом: заказчику видны только
// видимые не-черновики). Группировка по слотам — на фронте. scope=with-projects
// (company): плюс документы всех сделок компании, у них project-ссылка.
export function entityDocumentsQueryOptions(
  kind: DocumentOwnerKind,
  id: string,
  scope?: 'with-projects',
) {
  return queryOptions({
    queryKey: documentKeys.byEntity(kind, id, scope),
    queryFn: async () => {
      const body = await apiRequest<{ items: unknown[] }>({
        method: 'GET',
        url: '/documents',
        query: { entity_kind: kind, entity_id: id, ...(scope ? { scope } : {}) },
      })
      return z.array(DocumentSummarySchema).parse(body.items)
    },
  })
}

export function documentDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: documentKeys.detail(id),
    queryFn: async () =>
      DocumentReadSchema.parse(await apiRequest({ method: 'GET', url: `/documents/${id}` })),
  })
}

export function documentVersionsQueryOptions(id: string) {
  return queryOptions({
    queryKey: documentKeys.versions(id),
    queryFn: async () => {
      const body = await apiRequest<{ items: unknown[] }>({
        method: 'GET',
        url: `/documents/${id}/versions`,
      })
      return z.array(DocumentVersionSchema).parse(body.items)
    },
  })
}

// Авто-фиксация просмотра заказчиком (side-effect страницы документа).
export function recordDocumentView(id: string): Promise<unknown> {
  return apiRequest({ method: 'POST', url: `/documents/${id}/views` })
}

// История просмотров (employee-срез) — keyset-листинг.
export function documentViewsQueryOptions(id: string) {
  return infiniteQueryOptions({
    queryKey: documentKeys.views(id),
    queryFn: async ({ pageParam }) => {
      const body = await apiRequest<CursorPage<unknown>>({
        method: 'GET',
        url: `/documents/${id}/views`,
        query: { ...(pageParam ? { cursor: pageParam } : {}), limit: 50 },
      })
      return {
        items: z.array(DocumentViewEntrySchema).parse(body.items),
        next_cursor: body.next_cursor ?? null,
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  })
}
